import React, { useState, useEffect, ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import FormGroup from "../../../../components/common/FormGroup";
import TextInput from "../../../../components/ui/TextInput";
import NumberInput from "../../../../components/common/NumberInput";
import Button from "../../../../components/ui/Button";
import { FileInput } from "../../../../components/common/FileInput";
import ImageSpecsBadges from "../../../../components/common/ImageSpecsBadges";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import ImageValidationInfo from "../../../../components/common/ImageValidationInfo";
import { useImageValidation, IMAGE_SPECS } from "../../../../hooks/useImageValidation";
import { createLevel, getProgressionConfig, getLevels } from "../../../../api/progression";
import { uploadFileToS3 } from "../../../../api/s3";
import { IMAGE_MAX_SIZE_200_KB, IMAGE_MAX_SIZE_1_MB, IMAGE_MAX_SIZE_300_KB } from "../../../../utils/image";
import styles from "./CreateLevel.module.css";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface CreateLevelProps {
    navigateToNext: () => void;
}

const CreateLevel: React.FC<CreateLevelProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const token = localStorage.getItem("token") || "";

    const linkId = trackData?.trackId || trackData?.id;

    const [name, setName] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [discoveredWaypointsThreshold, setDiscoveredWaypointsThreshold] = useState<number>(1);
    const [imageUrl, setImageUrl] = useState<string>("");
    const [hasAvatarEvolution, setHasAvatarEvolution] = useState<boolean>(false);
    const [progressionImageOverride, setProgressionImageOverride] = useState<string>("");
    const [avatarImageOverride, setAvatarImageOverride] = useState<string>("");
    const [progressionConfigId, setProgressionConfigId] = useState<string | null>(null);
    const [nextPosition, setNextPosition] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadingImage, setUploadingImage] = useState<boolean>(false);
    const [uploadingProgression, setUploadingProgression] = useState<boolean>(false);
    const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
    const [progressionSizeError, setProgressionSizeError] = useState<string | null>(null);
    const [avatarSizeError, setAvatarSizeError] = useState<string | null>(null);

    // Use the image validation hooks
    const imageValidation = useImageValidation({
        type: "square",
        imageUrl: imageUrl || undefined,
    });
    const progressionValidation = useImageValidation({
        type: "progression",
        imageUrl: progressionImageOverride,
    });
    const avatarValidation = useImageValidation({
        type: "avatar",
        imageUrl: avatarImageOverride,
    });

    useEffect(() => {
        const loadData = async () => {
            if (!trackData.trackId) return;

            try {
                const configResponse = await getProgressionConfig({ token, trackId: trackData.trackId });
                if (configResponse?.progressionConfig) {
                    setProgressionConfigId(configResponse.progressionConfig.id);
                    const levelsResponse = await getLevels({
                        token,
                        progressionConfigId: configResponse.progressionConfig.id,
                    });
                    if (levelsResponse?.levels) {
                        const maxPosition =
                            levelsResponse.levels.length > 0
                                ? Math.max(...levelsResponse.levels.map((l) => l.position))
                                : 0;
                        setNextPosition(maxPosition + 1);
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
            }
        };

        loadData();
    }, [trackData.trackId, token]);

    const handleImageUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingImage(true);
        await imageValidation.validateImage(file);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setImageUrl(response.url);
            }
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleProgressionImageUpload = async (file: File) => {
        if (!file) return;

        if (file.size > IMAGE_MAX_SIZE_1_MB) {
            setProgressionSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize1Mb") }));
            return;
        }
        setProgressionSizeError(null);

        setUploadingProgression(true);
        await progressionValidation.validateImage(file);

        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setProgressionImageOverride(response.url);
            }
        } catch (error) {
            console.error("Error uploading progression image:", error);
        } finally {
            setUploadingProgression(false);
        }
    };

    const handleAvatarImageUpload = async (file: File) => {
        if (!file) return;

        if (file.size > IMAGE_MAX_SIZE_300_KB) {
            setAvatarSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize300kb") }));
            return;
        }
        setAvatarSizeError(null);

        setUploadingAvatar(true);
        await avatarValidation.validateImage(file);

        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setAvatarImageOverride(response.url);
            }
        } catch (error) {
            console.error("Error uploading avatar image:", error);
        } finally {
            setUploadingAvatar(false);
        }
    };

    // Dropzone for progression image
    const {
        getRootProps: getProgressionRootProps,
        getInputProps: getProgressionInputProps,
        isDragActive: isProgressionDragActive,
    } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                handleProgressionImageUpload(acceptedFiles[0]);
            }
        },
        onDropRejected: () => {
            setProgressionSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize1Mb") }));
        },
        accept: { "image/*": [".svg", ".png", ".jpg", ".jpeg", ".webp"] },
        maxSize: IMAGE_MAX_SIZE_1_MB,
        disabled: uploadingProgression,
        noClick: true,
    });

    // Dropzone for avatar image
    const {
        getRootProps: getAvatarRootProps,
        getInputProps: getAvatarInputProps,
        isDragActive: isAvatarDragActive,
    } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                handleAvatarImageUpload(acceptedFiles[0]);
            }
        },
        onDropRejected: () => {
            setAvatarSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize300kb") }));
        },
        accept: { "image/*": [".svg", ".png", ".jpg", ".jpeg", ".webp"] },
        maxSize: IMAGE_MAX_SIZE_300_KB,
        disabled: uploadingAvatar,
        noClick: true,
    });

    const handleSave = async () => {
        if (!progressionConfigId || !name || !text || !imageUrl) return;

        setLoading(true);
        try {
            await createLevel({
                token,
                progressionConfigId,
                position: nextPosition,
                name,
                text: text || undefined,
                discoveredWaypointsThreshold,
                imageUrl: imageUrl || undefined,
                hasAvatarEvolution,
                progressionImageOverride: hasAvatarEvolution ? progressionImageOverride || undefined : undefined,
                avatarImageOverride: hasAvatarEvolution ? avatarImageOverride || undefined : undefined,
                language: trackData.language || "EN",
            });
            navigateToNext();
        } catch (error) {
            console.error("Error creating level:", error);
        } finally {
            setLoading(false);
        }
    };

    const isUploading = uploadingImage || uploadingProgression || uploadingAvatar;

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/avatar`}>{t("progression")}</Link>
                <span>›</span>
                <Link to={`/create/tour/${linkId}/levels`}>{t("levels")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("createLevel")}</span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("createLevel")}</h2>
                        <p className={pageHeader.subtitle}>{t("createLevelDescription")}</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className={styles.sectionCard}>
                {/* Level number */}
                <FormGroup title={t("levelNumber") + " *"} subtitle={t("levelNumberDescription")} required>
                    <NumberInput value={nextPosition} onChange={() => {}} min={1} size="medium" readOnly disabled />
                </FormGroup>

                {/* Level name */}
                <FormGroup
                    title={t("levelName")}
                    subtitle={t("levelNameDescription")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput
                        placeholder={t("enterLevelName")}
                        value={name}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setName(e.target.value)}
                        size="medium"
                    />
                </FormGroup>

                {/* Image */}
                <FormGroup title={t("image")} subtitle={t("levelImageDescription")} required>
                    <ImageSpecsBadges spec="square" warnings={imageValidation.warnings} />
                    {imageUrl && (
                        <div style={{ marginBottom: "12px" }}>
                            <img src={imageUrl} alt={t("levelImage")} className={styles.previewImage} />
                        </div>
                    )}
                    <FileInput
                        onFileSelected={handleImageUpload}
                        placeholder={uploadingImage ? t("uploading") : t("dragDropOrClick")}
                        disabled={uploadingImage}
                        acceptedFileTypes={{ "image/*": [".png", ".jpg", ".jpeg", ".webp"] }}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </FormGroup>

                {/* Number of objects to discover */}
                <FormGroup
                    title={t("numberOfObjectsToDiscover")}
                    subtitle={t("numberOfObjectsToDiscoverDescription")}
                    required
                >
                    <NumberInput
                        placeholder={t("enterNumber")}
                        value={discoveredWaypointsThreshold}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setDiscoveredWaypointsThreshold(parseInt(e.target.value) || 1)
                        }
                        min={1}
                        size="medium"
                    />
                </FormGroup>

                {/* Text */}
                <FormGroup
                    title={t("text")}
                    subtitle={t("levelTextDescription")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput
                        placeholder={t("enterText")}
                        value={text}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setText(e.target.value)}
                        size="big"
                        numberOfLines={4}
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            padding: "12px",
                            width: "100%",
                            boxSizing: "border-box",
                        }}
                    />
                </FormGroup>

                {/* Avatar evolution (optional) */}
                <div className={styles.avatarEvolutionSection}>
                    <h3 className={styles.sectionTitle}>{t("avatarEvolutionOptional")}</h3>
                    <p className={styles.sectionDescription}>{t("avatarEvolutionDescription")}</p>

                    <div className={styles.avatarEvolutionToggle}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <ToggleSwitch
                                checked={hasAvatarEvolution}
                                onChange={() => setHasAvatarEvolution(!hasAvatarEvolution)}
                            />
                            <span style={{ fontSize: "14px", color: "#1a1a2e" }}>
                                {t("enableEvolutionForThisLevel")}
                            </span>
                        </div>
                    </div>

                    {hasAvatarEvolution && (
                        <>
                            {/* 1) Progression illustration */}
                            <div style={{ marginTop: "24px" }}>
                                <h4 className={styles.sectionTitle}>1) {t("progressionIllustration")}</h4>
                                <p className={styles.sectionDescription}>{t("progressionIllustrationDescription")}</p>

                                <div className={styles.specsContainer}>
                                    <span className={styles.specBadge}>
                                        {t("avatarRatio")}: {IMAGE_SPECS.progression.ratioLabel}
                                    </span>
                                    <span className={styles.specBadge}>
                                        {t("avatarWeight")}: {t("imageMaxSize1Mb")} max.
                                    </span>
                                </div>

                                <div
                                    {...getProgressionRootProps()}
                                    className={`${styles.uploadArea} ${progressionImageOverride ? styles.hasImage : ""} ${isProgressionDragActive ? styles.dragActive : ""}`}
                                >
                                    <input {...getProgressionInputProps()} />
                                    {progressionImageOverride ? (
                                        <>
                                            <img
                                                src={progressionImageOverride}
                                                alt={t("progressionImage")}
                                                className={styles.previewImage}
                                            />
                                            <Button
                                                label={t("chooseFile")}
                                                onClick={() => {
                                                    const input = document.createElement("input");
                                                    input.type = "file";
                                                    input.accept = "image/*";
                                                    input.onchange = (e: any) => {
                                                        if (e.target.files?.[0]) {
                                                            handleProgressionImageUpload(e.target.files[0]);
                                                        }
                                                    };
                                                    input.click();
                                                }}
                                                disabled={uploadingProgression}
                                                className="btn-outline"
                                            />
                                        </>
                                    ) : (
                                        <p className={styles.uploadAreaText}>{t("dragDropImageHere")}</p>
                                    )}
                                </div>

                                <ImageValidationInfo warnings={progressionValidation.warnings} />
                                {progressionSizeError && (
                                    <p style={{ color: "red", marginTop: "10px" }}>{progressionSizeError}</p>
                                )}

                                {!progressionImageOverride && (
                                    <div style={{ marginTop: "12px" }}>
                                        <Button
                                            label={t("chooseFile")}
                                            onClick={() => {
                                                const input = document.createElement("input");
                                                input.type = "file";
                                                input.accept = "image/*";
                                                input.onchange = (e: any) => {
                                                    if (e.target.files?.[0]) {
                                                        handleProgressionImageUpload(e.target.files[0]);
                                                    }
                                                };
                                                input.click();
                                            }}
                                            disabled={uploadingProgression}
                                            className="btn-outline"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* 2) Avatar miniature */}
                            <div style={{ marginTop: "32px" }}>
                                <h4 className={styles.sectionTitle}>2) {t("avatarMiniature")}</h4>
                                <p className={styles.sectionDescription}>{t("avatarMiniatureDescription")}</p>

                                <div className={styles.specsContainer}>
                                    <span className={styles.specBadge}>
                                        {t("avatarRatio")}: {IMAGE_SPECS.avatar.ratioLabel}
                                    </span>
                                    <span className={styles.specBadge}>
                                        {t("avatarWeight")}: {t("imageMaxSize300kb")} max.
                                    </span>
                                </div>

                                <div
                                    {...getAvatarRootProps()}
                                    className={`${styles.uploadArea} ${avatarImageOverride ? styles.hasImage : ""} ${isAvatarDragActive ? styles.dragActive : ""}`}
                                >
                                    <input {...getAvatarInputProps()} />
                                    {avatarImageOverride ? (
                                        <>
                                            <img
                                                src={avatarImageOverride}
                                                alt={t("avatarImage")}
                                                className={styles.previewImage}
                                            />
                                            <Button
                                                label={t("chooseFile")}
                                                onClick={() => {
                                                    const input = document.createElement("input");
                                                    input.type = "file";
                                                    input.accept = "image/*";
                                                    input.onchange = (e: any) => {
                                                        if (e.target.files?.[0]) {
                                                            handleAvatarImageUpload(e.target.files[0]);
                                                        }
                                                    };
                                                    input.click();
                                                }}
                                                disabled={uploadingAvatar}
                                                className="btn-outline"
                                            />
                                        </>
                                    ) : (
                                        <p className={styles.uploadAreaText}>{t("dragDropImageHere")}</p>
                                    )}
                                </div>

                                <ImageValidationInfo warnings={avatarValidation.warnings} />
                                {avatarSizeError && (
                                    <p style={{ color: "red", marginTop: "10px" }}>{avatarSizeError}</p>
                                )}

                                {!avatarImageOverride && (
                                    <div style={{ marginTop: "12px" }}>
                                        <Button
                                            label={t("chooseFile")}
                                            onClick={() => {
                                                const input = document.createElement("input");
                                                input.type = "file";
                                                input.accept = "image/*";
                                                input.onchange = (e: any) => {
                                                    if (e.target.files?.[0]) {
                                                        handleAvatarImageUpload(e.target.files[0]);
                                                    }
                                                };
                                                input.click();
                                            }}
                                            disabled={uploadingAvatar}
                                            className="btn-outline"
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Footer buttons — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={loading ? t("saving") : t("create")}
                    onClick={handleSave}
                    disabled={!name || !text || !imageUrl || loading || isUploading}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default CreateLevel;
